import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useFetch } from "@/lib/api";
import { Loading } from "@/components/common";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function PlanCard({ p, dark }) {
  const term = p.billing_term_months || 3;
  return (
    <div data-testid={`plan-card-${p.id}`} className={`p-8 border flex flex-col ring-2 ring-red-600 ${dark ? "border-gray-700 bg-gray-950" : "border-gray-200 bg-white"}`}>
      <div className="eyebrow mb-2">One simple plan</div>
      <h3 className={`text-lg font-bold ${dark ? "text-white" : ""}`}>{p.name}</h3>
      <div className="mt-4"><span className="text-5xl font-black font-display" data-testid="plan-price">{money(p.price_monthly)}</span><span className={`text-sm ${dark ? "text-gray-400" : "text-gray-500"}`}> / {p.unit} / month</span></div>
      <div className={`mt-2 text-sm ${dark ? "text-gray-400" : "text-gray-600"}`} data-testid="plan-term">Billed every {term} months in advance: {money(p.price_monthly * term)} per {p.unit} per quarter</div>
      <ul className={`mt-6 space-y-2 text-sm flex-1 ${dark ? "text-gray-300" : "text-gray-600"}`}>
        {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />{f}</li>)}
      </ul>
      <Link to="/register" data-testid={`plan-cta-${p.id}`} className="mt-8 text-center py-2.5 font-semibold text-sm bg-red-600 text-white hover:bg-red-700 transition-colors">Start 15-day trial</Link>
    </div>
  );
}

function HowBilling({ p, dark }) {
  const rows = [["15-day trial", `Endpoints used during the trial are billed per day (${money(p.price_monthly / 30)} per endpoint per day) on your first statement.`],
    [`${money(p.price_monthly)} per endpoint / month`, `Billed every ${p.billing_term_months || 3} months in advance when the trial ends. Endpoints added mid-quarter are billed for that full quarter on the next statement. No credit for removed endpoints.`],
    [`${money(p.escalation_price)} per human escalation`, "Charged once for each incident a person handles: automatic escalation, your request, or a technician assigned. Added to your next statement, including during the trial."],
    ["Weekly summary", "Every Monday you get a summary of completed tasks, incidents and escalations."]];
  return (
    <div className={`p-8 border ${dark ? "border-gray-700 text-gray-300" : "border-gray-200 text-gray-600"}`} data-testid="billing-explainer">
      <h3 className={`font-bold mb-5 ${dark ? "text-white" : "text-gray-900"}`}>How billing works</h3>
      <div className="space-y-5">{rows.map(([h, t]) => <div key={h}><div className={`font-semibold text-sm ${dark ? "text-white" : "text-gray-900"}`}>{h}</div><div className="text-sm mt-1">{t}</div></div>)}</div>
    </div>
  );
}

export function PricingCards({ dark = false }) {
  const { data, loading } = useFetch("/billing/plans");
  if (loading) return <Loading />;
  const p = (data || []).find((x) => x.id === "autopilot") || (data || [])[0];
  if (!p) return null;
  return <div className="grid lg:grid-cols-2 gap-6" data-testid="pricing-cards"><PlanCard p={p} dark={dark} /><HowBilling p={p} dark={dark} /></div>;
}

export default function Pricing() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-20">
      <div className="eyebrow mb-3">Pricing</div>
      <h1 className="text-4xl sm:text-5xl font-black">Pay per managed endpoint.</h1>
      <p className="mt-4 text-gray-600 max-w-2xl">One plan with every feature. Start with a 15-day trial, then pay per endpoint every quarter. Escalations to a human technician are billed separately.</p>
      <div className="mt-12"><PricingCards /></div>
      <div className="mt-10 border border-amber-200 bg-amber-50 text-amber-800 text-sm p-4" data-testid="billing-notice">Online card payment isn't available yet. When your trial ends we'll email a statement with payment instructions.</div>
    </div>
  );
}
